import ChatwootWidget from '@/common/ChatwootWidget';
import PageLoadIndicator from '@/common/PageLoadIndicator';
import '@/styles/globals.css';
import MiscUtils from '@/utils/MiscUtils';
import * as snippet from '@segment/snippet';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { QueryClient, defaultShouldDehydrateQuery } from '@tanstack/react-query';
import type { Query } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Persister, PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';
import { EvervaultProvider } from '@evervault/react';
import { NextPage } from 'next';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import Script from 'next/script';
import { ReactElement, ReactNode } from 'react';
import { DownloadAppModalProvider, DownloadAppModal } from '@/common/DownloadAppModal';
import { LoginModalProvider, LoginModal } from '@/common/LoginModal';

// Initializing DayJS

dayjs.extend(utc);
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);

dayjs.updateLocale('en', {
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: 'a few seconds',
    m: '1m',
    mm: '%dm',
    h: '1h',
    hh: '%dh',
    d: '1d',
    dd: '%dd',
    M: '1mo',
    MM: '%dmo',
    y: '1y',
    yy: '%dy',
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      cacheTime: 1000 * 60 * 60 * 24, // 24 hours
    },
  },
});

let persister: Persister;

if (MiscUtils.isBrowser()) {
  persister = createSyncStoragePersister({ storage: window.localStorage });
}

/** Firebase Auth is the source of truth; do not persist useAuthUser (avoids stale/corrupt User JSON). */
function shouldDehydrateQuery(query: Query) {
  if (query.queryKey[0] === 'authUser') {
    return false;
  }
  return defaultShouldDehydrateQuery(query);
}

function renderSnippet() {
  const opts = {
    apiKey: process.env.NEXT_PUBLIC_ANALYTICS_WRITE_KEY,
    // note: the page option only covers SSR tracking.
    // Page.js is used to track other events using `window.analytics.page()`
    page: true,
  };

  if (process.env.NODE_ENV === 'development') {
    return snippet.max(opts);
  }

  return snippet.min(opts);
}

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

export default function App({ Component, pageProps }: AppPropsWithLayout) {
  const getLayout = Component.getLayout ?? ((page) => page);

  return (
    <>
      {/* @ts-ignore - TypeScript compatibility issue with React 18 types */}
      <Head>
        <title>Bloom - Learn to Invest</title>

        {/* <!-- Favicon --> */}
        <link rel="icon" type="image/svg+xml" href="/favicon.ico" />

        {/* <!-- Primary Meta Tags --> */}
        <meta charSet="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="title" content="Bloom - Learn to Invest" />
        <meta
          name="description"
          content="Bloom offers zero-commission stock investing for young investors aged 13+. With built-in parental controls, education modules, fractional trading, and more, Bloom offers the safest way for teens to start building wealth early."
        />
        <meta
          name="keywords"
          content="Bloom, Investing for teens, Investing for beginners, how to invest, learn to invest, how the market works, crypto, how to day trade, what is a stock, what is the stock market."
        />
        <meta name="robots" content="index, follow" />
        <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
        <meta name="language" content="English" />
        <meta name="author" content="Bloom Investment Advisers, LLC" />

        {/* <!-- Open Graph / Facebook --> */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={process.env.NEXT_PUBLIC_APP_URL} />
        <meta property="og:title" content="Bloom - Learn to Invest" />
        <meta
          property="og:description"
          content="Bloom offers zero-commission stock investing for young investors aged 13+. With built-in parental controls, education modules, fractional trading, and more, Bloom offers the safest way for teens to start building wealth early."
        />
        <meta
          property="og:image"
          content={`${process.env.NEXT_PUBLIC_CDN_URL}/images/bloominvest_seo_image.png`}
        />

        {/* <!-- Twitter --> */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={process.env.NEXT_PUBLIC_APP_URL} />
        <meta property="twitter:title" content="Bloom - Learn to Invest" />
        <meta
          property="twitter:description"
          content="Bloom offers zero-commission stock investing for young investors aged 13+. With built-in parental controls, education modules, fractional trading, and more, Bloom offers the safest way for teens to start building wealth early."
        />
        <meta
          property="twitter:image"
          content={`${process.env.NEXT_PUBLIC_CDN_URL}/images/bloominvest_seo_image.png`}
        />

        {/* <!-- Apple Touch Icons --> */}
        <link rel="apple-touch-icon" href="apple-touch-icon.png" />

        <link
          rel="preload"
          href={`${process.env.NEXT_PUBLIC_CDN_URL}/fonts/SF-Pro-Rounded-Regular.woff2`}
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          rel="preload"
          href={`${process.env.NEXT_PUBLIC_CDN_URL}/fonts/SF-Pro-Rounded-Medium.woff2`}
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          rel="preload"
          href={`${process.env.NEXT_PUBLIC_CDN_URL}/fonts/SF-Pro-Rounded-Semibold.woff2`}
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          rel="preload"
          href={`${process.env.NEXT_PUBLIC_CDN_URL}/fonts/SF-Pro-Rounded-Bold.woff2`}
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
      </Head>
      {/* <ChatwootWidget /> */}
      <Script id="rudderstack">
        {`rudderanalytics=window.rudderanalytics=[];for(var methods=["load","page","track","alias","group","identify","ready","reset"],i=0;i<methods.length;i++){var method=methods[i];rudderanalytics[method]=function(d){return function(){rudderanalytics.push([d,...arguments])}}(method)}rudderanalytics.load("2Psr6La1hzk3I7DuufuvJJSh5jJ","https://rudderstack.joinbloom.co"),rudderanalytics.page();`}
      </Script>
      <Script id="rudderstack-cdn" src="https://cdn.rudderlabs.com/v1/rudder-analytics.min.js" />
      <Script id="segment-script" dangerouslySetInnerHTML={{ __html: renderSnippet() }} />
      {/* @ts-ignore - TypeScript compatibility issue with React 18 types */}
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{ persister, dehydrateOptions: { shouldDehydrateQuery } }}
      >
        {/* @ts-ignore - TypeScript compatibility issue with React 18 types */}
        <EvervaultProvider
          teamId={process.env.NEXT_PUBLIC_EVERVAULT_TEAM_ID ?? ''}
          appId={process.env.NEXT_PUBLIC_EVERVAULT_APP_ID ?? ''}
        >
          <LoginModalProvider>
            <DownloadAppModalProvider>
              {/* @ts-ignore - TypeScript compatibility issue with React 18 types */}
              {getLayout(<Component {...pageProps} />)}
              <LoginModal />
              <DownloadAppModal />
            </DownloadAppModalProvider>
          </LoginModalProvider>
        </EvervaultProvider>
        <ReactQueryDevtools initialIsOpen={false} />
        <PageLoadIndicator />
      </PersistQueryClientProvider>
    </>
  );
}
